import { Box, Divider, Flex, Text, Tooltip, useTheme } from '@chakra-ui/react';
import React, { Fragment } from 'react';
import { InfoIcon, RightIcon } from '../../../icons';
import { StyledLink } from '../../../StyledLink';
import GoalLoadingCell from '../LoadingCell/GoalLoadingCell';
import GoalDetails from '../../../GoalsTable/GoalDetails';
import { GoalStatusSummaryItem } from '../../types/team-table.types';

interface GoalsColProps {
  goalScope: 'CONTRIBUTOR' | 'TEAM';
  associatedIds?: string[];
  teamParentMap?: Record<string, string | null>;
  goalsTeamDataLoading?: boolean;
  expanded: string[];
  goalStatusSummary: GoalStatusSummaryItem[];
  goalStatusSummaryLoading: boolean;
  hasRightBorder?: boolean;
}
function GoalsCol({
  goalScope,
  goalsTeamDataLoading,
  goalStatusSummary,
  goalStatusSummaryLoading,
  expanded,
  teamParentMap,
  hasRightBorder,
}: GoalsColProps) {
  const { colors } = useTheme();
  return (
    <Flex
      direction="column"
      position="sticky"
      left={0}
      bg="white"
      h={'fit-content'}
      minW="400px"
      zIndex={79}
    >
      <Flex
        bg="#FBFBFB"
        position="sticky"
        top={0}
        borderLeft="1px solid #E0E0E0"
        borderRight={hasRightBorder ? '1px solid #E0E0E0' : undefined}
        align="center"
        minH="52px"
        px={4}
        zIndex={50}
        w={'full'}
      >
        <Flex w="full" align="center" gap={1}>
          <Text
            fontSize="sm"
            fontFamily="heading"
            fontWeight="medium"
            color="text.secondary2"
            flex={1}
          >
            Goals
          </Text>{' '}
          <Tooltip
            label="Goals are the main objectives that contributors aim to achieve. They are typically set for a specific period and are used to track progress and measure success."
            hasArrow
          >
            <Box>
              <InfoIcon />
            </Box>
          </Tooltip>
        </Flex>
      </Flex>

      <Divider />

      {goalsTeamDataLoading ||
      goalStatusSummaryLoading ||
      goalStatusSummary?.length === 0
        ? [...Array(10)].map((_, index) => (
            <GoalLoadingCell goalScope={goalScope} key={index} />
          ))
        : goalStatusSummary
            ?.filter(
              (row: GoalStatusSummaryItem) =>
                expanded.includes(teamParentMap?.[row.name] || '') ||
                teamParentMap?.[row.name] === null
            )
            ?.map((goalData: GoalStatusSummaryItem) => (
              <Fragment key={goalData?.name}>
                <Flex
                  minH={
                    goalScope === 'CONTRIBUTOR'
                      ? '164px'
                      : goalScope === 'TEAM'
                      ? '184px'
                      : '200px'
                  }
                  p={3}
                  bg="white"
                  borderLeft="1px solid"
                  borderRight={hasRightBorder ? '1px solid #E0E0E0' : undefined}
                  borderColor={'text.tertiary'}
                  gap={3}
                  direction={'column'}
                >
                  <Flex gap={8}>
                    <GoalDetails
                      data={goalData?.goalStatusData}
                      title="Goal(s)"
                      origin={goalScope === 'TEAM' ? 'team-table' : ''}
                      isLoading={goalStatusSummaryLoading}
                      isGoal
                    />
                  </Flex>
                  {goalData?.goalStatusData?.totalCount !== 0 ? (
                    <Box w={'fit-content'}>
                      <StyledLink
                        path={
                          goalScope === 'CONTRIBUTOR'
                            ? `/contributors/${goalData?.name}?tab=Goals`
                            : `/teams/${goalData.name}?tab=teamGoals`
                        }
                        fontSize="14px"
                        state={{
                          from: 'goals',
                        }}
                      >
                        <Flex align={'center'} gap={1} w={'fit-content'}>
                          View Goals
                          <RightIcon
                            height="14px"
                            width="14px"
                            color={colors.primary}
                            strokeWidth="3"
                          />
                        </Flex>
                      </StyledLink>
                    </Box>
                  ) : null}
                </Flex>
                <Divider />
              </Fragment>
            ))}
    </Flex>
  );
}

export default GoalsCol;
